import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { templatesService, type Template } from '@/services/templates.service';
import { uploadsService } from '@/services/uploads.service';
import type { Lead } from '@/services/leads.service';
import { Search, FileText, ArrowLeft, Image as ImageIcon, Video, File as FileIcon, MapPin, Upload, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

/** Extract named variables ({{firstName}}) from the body + header text. */
function extractVariables(t: Template): string[] {
  const src = `${t.headerText ?? ''}\n${t.body}`;
  const out: string[] = [];
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src))) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
}

/** Best-effort prefill from what we know about the lead. */
function prefillFor(varName: string, lead: Lead): string {
  const k = varName.toLowerCase();
  const firstName = lead.profileName?.trim().split(/\s+/)[0] ?? '';
  if (k === 'firstname' || k === 'first_name' || k === 'name' || k === 'customer_name' || k === 'customername') {
    return lead.profileName?.trim() && (k === 'name' || k.includes('customer'))
      ? lead.profileName.trim()
      : firstName;
  }
  if (k.includes('phone')) return lead.phone;
  return lead.collectedFields?.[varName] ?? '';
}

const HEADER_ICON: Record<string, React.ReactNode> = {
  IMAGE: <ImageIcon size={11} />,
  VIDEO: <Video size={11} />,
  DOCUMENT: <FileIcon size={11} />,
  LOCATION: <MapPin size={11} />,
};

interface Props {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  /** Called after a successful send so the parent can reload the thread. */
  onSent: () => void;
}

/**
 * Approved-template picker for the chat composer. Two steps: pick a template,
 * then fill its variables (prefilled from the lead where possible) and send.
 * Template sends are allowed outside the 24h window — this is the agent's way
 * to re-open an expired conversation.
 */
export function TemplatePickerModal({ open, onClose, lead, onSent }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  // Per-send image override — used when the template's stored image is a Meta
  // handle (synced templates have no public URL) or the agent wants another image.
  const [overrideImageUrl, setOverrideImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSearch('');
    setLoading(true);
    templatesService
      .list({ status: 'APPROVED', limit: 100 })
      .then((r) => setTemplates(r.items))
      .catch(() => { /* interceptor surfaces */ })
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(q) || t.body.toLowerCase().includes(q),
    );
  }, [templates, search]);

  const variables = useMemo(() => (selected ? extractVariables(selected) : []), [selected]);

  const pick = (t: Template) => {
    setSelected(t);
    setOverrideImageUrl(null);
    const initial: Record<string, string> = {};
    for (const v of extractVariables(t)) initial[v] = prefillFor(v, lead);
    setVars(initial);
  };

  const isHttpUrl = (u?: string) => !!u && /^https?:\/\//i.test(u);
  const effectiveImageUrl = overrideImageUrl
    ?? (selected?.headerType === 'IMAGE' && isHttpUrl(selected.headerImageUrl) ? selected!.headerImageUrl : undefined);
  // Synced-from-Meta templates store a media HANDLE, not a URL — the send
  // would fail, so the agent must upload an image first.
  const imageRequired = selected?.headerType === 'IMAGE' && !effectiveImageUrl;

  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image file'); return; }
    setUploadingImage(true);
    try {
      const r = await uploadsService.imagekit(file, undefined, 'template-send-header');
      setOverrideImageUrl(r.url);
    } catch { /* interceptor toasts */ } finally { setUploadingImage(false); }
  };

  const preview = useMemo(() => {
    if (!selected) return '';
    return selected.body.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) => vars[key] || `{{${key}}}`);
  }, [selected, vars]);

  const send = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const r = await templatesService.sendToLead(selected.templateUid, {
        leadUid: lead.leadUid,
        variables: vars,
        ...(overrideImageUrl ? { headerOverride: { imageUrl: overrideImageUrl } } : {}),
      });
      if (r.ok) {
        toast.success('Template sent');
        onSent();
        onClose();
      } else {
        toast.error(r.error ?? 'Send failed');
      }
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Send a template" widthClass="max-w-xl">
      {!selected && (
        <div className="space-y-3">
          <Input
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            iconLeft={<Search size={14} />}
          />
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {loading && [0, 1, 2].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
            {!loading && filtered.length === 0 && (
              <p className="py-8 text-center text-sm text-text-muted">
                No approved templates{search ? ' match your search' : ' yet — create one under Templates'}.
              </p>
            )}
            {filtered.map((t) => (
              <button
                key={t.templateUid}
                onClick={() => pick(t)}
                className="flex w-full flex-col gap-1 rounded-xl border border-border bg-surface px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-surface-alt"
              >
                <div className="flex items-center gap-2">
                  <FileText size={13} className="shrink-0 text-primary" />
                  <span className="truncate text-sm font-semibold text-text-primary">{t.name}</span>
                  <span className="ml-auto shrink-0 rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs uppercase text-text-muted">
                    {t.language}
                  </span>
                  {t.headerType && t.headerType !== 'TEXT' && (
                    <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs text-text-muted">
                      {HEADER_ICON[t.headerType]} {t.headerType.toLowerCase()}
                    </span>
                  )}
                </div>
                <p className="line-clamp-2 text-xs text-text-muted">{t.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <div className="space-y-4">
          <button
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-primary"
          >
            <ArrowLeft size={12} /> Choose a different template
          </button>

          {variables.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-text-primary">Fill in the variables</p>
              {variables.map((v) => (
                <div key={v} className="flex items-center gap-2">
                  <span className="w-32 shrink-0 truncate font-mono text-xs text-text-muted">{`{{${v}}}`}</span>
                  <Input
                    value={vars[v] ?? ''}
                    onChange={(e) => setVars((prev) => ({ ...prev, [v]: e.target.value }))}
                    placeholder={`Value for ${v}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Preview bubble — mirrors what the customer receives */}
          <div>
            <p className="mb-1.5 text-xs font-semibold text-text-primary">Preview</p>
            <div className="rounded-2xl rounded-br-sm bg-primary-soft px-3.5 py-2.5 text-[13.5px] leading-snug text-primary whitespace-pre-wrap break-words">
              {selected.headerType === 'IMAGE' && (
                effectiveImageUrl ? (
                  <img src={effectiveImageUrl} alt="" className="mb-1.5 max-h-44 w-full rounded-lg object-cover" />
                ) : (
                  <div className="mb-1.5 flex h-24 items-center justify-center rounded-lg bg-black/10 text-xs">
                    <ImageIcon size={14} className="mr-1.5" /> No usable image — upload one below
                  </div>
                )
              )}
              {selected.headerType === 'VIDEO' && (
                <div className="mb-1.5 flex h-16 items-center justify-center gap-1.5 rounded-lg bg-black/60 text-xs text-white">
                  <Video size={13} /> Video header
                </div>
              )}
              {selected.headerType === 'DOCUMENT' && (
                <div className="mb-1.5 flex items-center gap-1.5 rounded-lg bg-black/10 px-2 py-1.5 text-xs">
                  <FileIcon size={13} /> {selected.headerDocumentFilename || 'Document'}
                </div>
              )}
              {selected.headerType === 'LOCATION' && (
                <div className="mb-1.5 flex items-center gap-1.5 rounded-lg bg-black/10 px-2 py-1.5 text-xs">
                  <MapPin size={13} /> Location
                </div>
              )}
              {selected.headerType === 'TEXT' && selected.headerText && (
                <p className="mb-1 font-semibold">{selected.headerText}</p>
              )}
              {preview}
              {selected.footer && <p className="mt-1 text-xs opacity-60">{selected.footer}</p>}
              {(selected.buttons ?? []).length > 0 && (
                <div className="mt-2 space-y-1 border-t border-primary/15 pt-1.5">
                  {selected.buttons.map((b, i) => (
                    <div key={i} className="rounded-md bg-surface/70 py-1 text-center text-[12px] font-medium">
                      {b.type === 'COPY_CODE' ? 'Copy code' : (b.text || b.type)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selected.headerType === 'IMAGE' && (
              <label className={clsx(
                'mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium transition-colors hover:border-primary/50 hover:text-primary',
                imageRequired ? 'text-danger border-danger/40' : 'text-text-secondary',
              )}>
                {uploadingImage ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploadingImage ? 'Uploading…' : overrideImageUrl ? 'Change image again' : imageRequired ? 'Upload an image (required for this send)' : 'Use a different image for this send'}
                <input type="file" accept="image/*" hidden disabled={uploadingImage}
                  onChange={(e) => { pickImage(e.target.files?.[0] ?? undefined); e.target.value = ''; }} />
              </label>
            )}
          </div>

          <div className={clsx('flex justify-end gap-2')}>
            <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button
              variant="primary"
              onClick={send}
              loading={sending}
              disabled={sending || uploadingImage || imageRequired || variables.some((v) => !(vars[v] ?? '').trim())}
            >
              Send to {lead.profileName?.trim() || lead.phone}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
